import React, { useCallback, useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Heading from "../../../../common/element/Heading";
import ClientInput from "../../../../common/element/Input/clientInput";
import BtnPrimary from "../../../../common/element/Buttons/btnPrimary";
import { debounce } from "../../../../common/snippet/Debouncing";
import { setBreadcrumbs } from "../../../../redux/Admin/breadcrumbsSlice";
import { Pagination } from "../../../../common/pagination";
import ModernPagination from "../../../../common/pagination/ModernPagination";
import LearningFeedbackList from "./LearningFeedbackList";
import QRCodeModal from "./QRCodeModal";
import { getLearningFeedbackList } from "../../../../redux/LearningFeedback/learningFeedbackSlice";
import {
  selectLearningFeedbackList,
  selectLearningFeedbackListTotal,
  selectLearningFeedbackListLoading,
} from "../../../../redux/LearningFeedback/learningFeedbackSelectors";

const LearningFeedback = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [filterpage, setfilterpage] = useState(1);
  const [filterlimit, setfilterlimit] = useState(10);
  const [qrModalOpen, setQrModalOpen] = useState(false);

  const list = useSelector(selectLearningFeedbackList);
  const totalCount = useSelector(selectLearningFeedbackListTotal);
  const loading = useSelector(selectLearningFeedbackListLoading);

  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;

  const [getFilterData, SetFilterData] = useState({ search: "" });

  const totalPages = Math.ceil(totalCount / filterlimit);

  const handleSearch = (e) => {
    SetFilterData((prev) => ({ ...prev, [e.target.name]: e.target.value.trim() }));
    setfilterpage(1);
  };

  useEffect(() => {
    dispatch(
      setBreadcrumbs([
        { label: "Learning & Feedback", url: "/learning-feedback", home: "/dashboard" },
      ])
    );
  }, [dispatch]);

  const fetchData = useCallback(() => {
    dispatch(
      getLearningFeedbackList({
        page: filterpage,
        limit: filterlimit,
        search: getFilterData.search,
        from_date: startDate ? dayjs(startDate).format("YYYY-MM-DD") : "",
        to_date: endDate ? dayjs(endDate).format("YYYY-MM-DD") : "",
      })
    );
  }, [dispatch, filterpage, filterlimit, getFilterData.search, startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const inputchangehandler = useCallback(debounce(handleSearch), []);

  const handlePageChange = (page) => setfilterpage(page);

  const handleView = (item) => navigate(`/learning-feedback/${item.id}`);

  return (
    <React.Fragment>
      <div>
        <p className="text-gray-600 my-3"></p>
      </div>

      <div className="flex justify-between mb-5">
        <div className="w-80 h-10 text-start">
          <Heading>Learning &amp; Feedback</Heading>
        </div>

        <div className="flex gap-2">
          <BtnPrimary
            onClick={() => setQrModalOpen(true)}
            className="h-9 flex items-center justify-center gap-2"
            style={{ backgroundColor: "#FF8E1C", borderColor: "#FF8E1C" }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="1" y="1" width="5" height="5" rx="0.5" stroke="white" strokeWidth="1.2" />
              <rect x="10" y="1" width="5" height="5" rx="0.5" stroke="white" strokeWidth="1.2" />
              <rect x="1" y="10" width="5" height="5" rx="0.5" stroke="white" strokeWidth="1.2" />
              <rect x="2.5" y="2.5" width="2" height="2" fill="white" />
              <rect x="11.5" y="2.5" width="2" height="2" fill="white" />
              <rect x="2.5" y="11.5" width="2" height="2" fill="white" />
              <path d="M10 10h1.5v1.5H10zM12 10h1.5v1.5H12zM10 12h1.5v1.5H10zM12 12h1.5v1.5H12z" fill="white" />
            </svg>
            Copy &amp; View QR
          </BtnPrimary>
        </div>
      </div>

      <div className="flex justify-end gap-3 mb-4">
        <div className="lf-datepicker input-wrapper relative">
          <div className="input-suffix-start left-3.5 z-10">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="1" y="2" width="14" height="12" rx="1" stroke="#9CA3AF" strokeWidth="1.2" />
              <path d="M1 6h14" stroke="#9CA3AF" strokeWidth="1.2" />
              <path d="M5 1v2M11 1v2" stroke="#9CA3AF" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
          </div>
          <DatePicker
            selectsRange
            startDate={startDate}
            endDate={endDate}
            onChange={(range) => {
              setDateRange(range);
              setfilterpage(1);
            }}
            dateFormat="dd/MM/yyyy"
            placeholderText="From - To"
            isClearable
            className="input pl-10 pr-8 w-72 input-shadow h-10 text-sm border-gray-300 rounded-lg border text-gray-700 leading-tight focus:outline-none"
          />
        </div>

        <div className="input-wrapper">
          <div className="input-suffix-start left-3.5">
            <img src="/assets/icons-svg/search-lg.svg" alt="" className="h-5" />
          </div>
          <ClientInput
            type="text"
            className="input pl-10 w-80 input-shadow h-10 text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            placeholder="Search"
            inputchangehandler={inputchangehandler}
            name="search"
          />
        </div>
      </div>

      <LearningFeedbackList
        sampleData={list}
        loading={loading}
        onView={handleView}
      />

      <div className="flex justify-between items-center mt-3">
        <Pagination
          totalCount={totalCount}
          pageNumber={filterpage}
          displayPage={list?.length}
          UpdatePageLimit={setfilterlimit}
          UpdatePage={setfilterpage}
        />
        {totalCount > 0 && totalPages > 1 && (
          <ModernPagination
            totalPages={totalPages}
            currentPage={filterpage}
            onPageChange={handlePageChange}
          />
        )}
      </div>

      <QRCodeModal isOpen={qrModalOpen} onClose={() => setQrModalOpen(false)} />
    </React.Fragment>
  );
};

export default LearningFeedback;
